/* =============================================================
   useDocumentTypes.js — CRUD for document_types table
   -------------------------------------------------------------
   Fetches all document types and exposes create / update /
   delete operations for the Settings → Document Types section.

   Module-level pub/sub cache:
     All hook instances share a single _cache. When any instance
     fetches or mutates, all other mounted instances update
     instantly — no page reload needed.

   required_for: string[] — which student categories require
   this document. Empty = required for all (default).
   ============================================================= */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth }   from '../context/AuthContext';

/* ─── MODULE-LEVEL SHARED CACHE ──────────────────────────────── */
// null  = never fetched yet (show loading spinner)
// []    = fetched, empty result
// [...] = fetched data
let _cache = null;
const _subs = new Set(); // Set of { setDocTypes, setTaskTypes, setLoading } per instance

function _broadcast(all) {
  _cache = all;
  _subs.forEach(({ setDocTypes, setTaskTypes, setLoading }) => {
    setDocTypes(all.filter(d => d.type !== 'task'));
    setTaskTypes(all.filter(d => d.type === 'task'));
    setLoading(false);
  });
}


export function useDocumentTypes() {
  const { user } = useAuth();

  // Initialize from cache immediately — no flash on remount
  const [docTypes,  setDocTypes]  = useState(() => (_cache ?? []).filter(d => d.type !== 'task'));
  const [taskTypes, setTaskTypes] = useState(() => (_cache ?? []).filter(d => d.type === 'task'));
  const [loading,   setLoading]   = useState(_cache === null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);

  /* ── Subscribe to shared cache updates ──────────────────────── */
  useEffect(() => {
    const sub = { setDocTypes, setTaskTypes, setLoading };
    _subs.add(sub);
    return () => _subs.delete(sub);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  /* ── fetchDocTypes ───────────────────────────────────────────── */
  const fetchDocTypes = useCallback(async () => {
    if (!user?.universityId) return;
    const { data, error: fetchError } = await supabase
      .from('document_types')
      .select('id, name, icon, required_for, template_storage_path, template_file_name, type')
      .eq('university_id', user.universityId)
      .order('name');
    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
    } else {
      _broadcast(data ?? []); // updates ALL mounted instances
    }
  }, [user?.universityId]);

  // Reset cache when university changes so a fresh fetch is triggered
  useEffect(() => { _cache = null; }, [user?.universityId]);

  useEffect(() => { fetchDocTypes(); }, [fetchDocTypes]);


  /* ── createDocType ─────────────────────────────────────────── */
  const createDocType = async ({ name, icon, requiredFor = [] }) => {
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase
      .from('document_types')
      .insert({ name: name.trim(), icon: icon || 'draft', required_for: requiredFor, type: 'document', university_id: user.universityId });
    if (insertError) { setError(insertError.message); setSaving(false); throw insertError; }
    await fetchDocTypes();
    setSaving(false);
  };


  /* ── createTaskType ─────────────────────────────────────────── */
  const createTaskType = async (name) => {
    if (!name?.trim()) return;
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase
      .from('document_types')
      .insert({ name: name.trim(), icon: 'check_box', required_for: [], type: 'task', university_id: user.universityId });
    if (insertError) { setError(insertError.message); setSaving(false); throw insertError; }
    await fetchDocTypes(); // _broadcast will update ProcessPanel too
    setSaving(false);
  };


  /* ── updateRequiredFor ─────────────────────────────────────── */
  const updateRequiredFor = async (id, requiredFor) => {
    setSaving(true);
    setError(null);
    const { error: updateError } = await supabase
      .from('document_types')
      .update({ required_for: requiredFor })
      .eq('id', id);
    if (updateError) { setError(updateError.message); setSaving(false); throw updateError; }
    await fetchDocTypes();
    setSaving(false);
  };


  /* ── deleteDocType ─────────────────────────────────────────── */
  // FK constraints on process_template_steps and process_steps use
  // ON DELETE SET NULL, so PostgreSQL handles cleanup automatically.
  const deleteDocType = async (id) => {
    setSaving(true);
    setError(null);
    const { error: deleteError } = await supabase
      .from('document_types')
      .delete()
      .eq('id', id);
    if (deleteError) { setError(deleteError.message); setSaving(false); throw deleteError; }
    await fetchDocTypes();
    setSaving(false);
  };


  /* ── uploadTemplate ────────────────────────────────────────── */
  const uploadTemplate = async (docTypeId, file) => {
    setSaving(true);
    setError(null);

    const existing = docTypes.find(d => d.id === docTypeId);
    if (existing?.template_storage_path) {
      await supabase.storage.from('documents').remove([existing.template_storage_path]);
    }

    const ext         = file.name.includes('.') ? file.name.split('.').pop() : 'bin';
    const storagePath = `templates/${docTypeId}/${crypto.randomUUID()}.${ext}`;

    const { error: storageError } = await supabase.storage
      .from('documents')
      .upload(storagePath, file, { contentType: file.type, upsert: false });

    if (storageError) { setError(storageError.message); setSaving(false); throw storageError; }

    const { error: dbError } = await supabase
      .from('document_types')
      .update({ template_storage_path: storagePath, template_file_name: file.name })
      .eq('id', docTypeId);

    if (dbError) {
      await supabase.storage.from('documents').remove([storagePath]);
      setError(dbError.message); setSaving(false); throw dbError;
    }

    await fetchDocTypes();
    setSaving(false);
  };


  /* ── removeTemplate ─────────────────────────────────────────── */
  const removeTemplate = async (docTypeId) => {
    setSaving(true);
    setError(null);

    const existing = docTypes.find(d => d.id === docTypeId);
    if (existing?.template_storage_path) {
      await supabase.storage.from('documents').remove([existing.template_storage_path]);
    }

    const { error: dbError } = await supabase
      .from('document_types')
      .update({ template_storage_path: null, template_file_name: null })
      .eq('id', docTypeId);

    if (dbError) { setError(dbError.message); setSaving(false); throw dbError; }

    await fetchDocTypes();
    setSaving(false);
  };


  return {
    docTypes, taskTypes, loading, saving, error,
    createDocType, createTaskType,
    deleteDocType,
    uploadTemplate, removeTemplate,
  };
}
